import { useAppStore } from "../store";
import type {
  ArtifactListResponse,
  CreateSessionRequest,
  FileUploadResponse,
  SavePlanResponse,
  SendMessageRequest,
  SendMessageResponse,
  SessionInfo,
  TenantListResponse,
  WorklogResponse,
} from "./types";
import {
  ArtifactListResponseSchema,
  FileUploadResponseSchema,
  SavePlanResponseSchema,
  SendMessageResponseSchema,
  SessionInfoSchema,
  TenantListResponseSchema,
  WorklogResponseSchema,
} from "./types";

class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(
    message: string,
    status: number,
    data?: unknown
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { apiUrl, apiToken } = useAppStore.getState();

  if (!apiToken) {
    throw new Error("API token not set");
  }

  const url = `${apiUrl}${endpoint}`;
  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${apiToken}`);
  headers.set("Content-Type", "application/json");

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {
      errorData = await response.text();
    }
    throw new ApiError(
      `API request failed: ${response.statusText}`,
      response.status,
      errorData
    );
  }

  return response.json() as Promise<T>;
}

export const api = {
  // Sessions
  async listSessions(limit = 50): Promise<SessionInfo[]> {
    const data = await apiRequest<{ sessions: SessionInfo[] }>(
      `/api/v1/sessions?limit=${limit}`
    );
    console.log(`[API] Loaded ${data.sessions.length} sessions from API`);
    // Safe parse with error handling - skip invalid sessions but log them
    return data.sessions.map((s) => {
      try {
        return SessionInfoSchema.parse(s);
      } catch (error) {
        console.error("[API] Failed to parse session:", s, error);
        // Return partial data with what we have
        return {
          session_id: s.session_id || "",
          tenant: s.tenant || "",
          session_dir: s.session_dir || "",
          created_at: s.created_at || "",
          last_activity: s.last_activity || null,
          last_modified: s.last_modified || null,
          first_message: s.first_message || null,
        } as SessionInfo;
      }
    });
  },

  async getSession(sessionId: string): Promise<SessionInfo> {
    const data = await apiRequest<SessionInfo>(`/api/v1/sessions/${sessionId}`);
    return SessionInfoSchema.parse(data);
  },

  async createSession(request: CreateSessionRequest): Promise<SessionInfo> {
    const data = await apiRequest<SessionInfo>("/api/v1/sessions", {
      method: "POST",
      body: JSON.stringify(request),
    });
    return SessionInfoSchema.parse(data);
  },

  // Worklog
  async getWorklog(sessionId: string): Promise<WorklogResponse> {
    const data = await apiRequest<WorklogResponse>(
      `/api/v1/sessions/${sessionId}/worklog`
    );
    return WorklogResponseSchema.parse(data);
  },

  // Messages
  async sendMessage(
    sessionId: string,
    request: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const data = await apiRequest<SendMessageResponse>(
      `/api/v1/sessions/${sessionId}/messages`,
      {
        method: "POST",
        body: JSON.stringify(request),
      }
    );
    return SendMessageResponseSchema.parse(data);
  },

  // Artifacts
  async listArtifacts(sessionId: string): Promise<ArtifactListResponse> {
    const data = await apiRequest<ArtifactListResponse>(
      `/api/v1/sessions/${sessionId}/artifacts`
    );
    return ArtifactListResponseSchema.parse(data);
  },


  // Tenants
  async listTenants(): Promise<TenantListResponse> {
    const data = await apiRequest<TenantListResponse>("/api/v1/tenants");
    return TenantListResponseSchema.parse(data);
  },

  // Save plan
  async savePlan(sessionId: string): Promise<SavePlanResponse> {
    const data = await apiRequest<SavePlanResponse>(
      `/api/v1/sessions/${sessionId}/save_plan`,
      {
        method: "POST",
      }
    );
    return SavePlanResponseSchema.parse(data);
  },

  // Version (for token validation)
  async getVersion(): Promise<{ service: string; version: string }> {
    const data = await apiRequest<{ service: string; version: string }>("/version");
    return data;
  },

  // Delete session
  async deleteSession(sessionId: string): Promise<{ success: boolean; message: string }> {
    const data = await apiRequest<{ success: boolean; message: string }>(
      `/api/v1/sessions/${sessionId}`,
      {
        method: "DELETE",
      }
    );
    return data;
  },

  // File upload
  async uploadFile(
    sessionId: string,
    file: File,
    fileType: "excel" | "csv"
  ): Promise<FileUploadResponse> {
    const { apiUrl, apiToken } = useAppStore.getState();

    if (!apiToken) {
      throw new Error("API token not set");
    }

    const formData = new FormData();
    formData.append("file", file);

    const url = `${apiUrl}/api/v1/sessions/${sessionId}/upload?file_type=${fileType}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new ApiError(
        `File upload failed: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return FileUploadResponseSchema.parse(data);
  },
};

export { ApiError };

